import {
	Component,
	Input,
	ElementRef,
	OnDestroy,
	Optional,
	Self,
	DoCheck,
} from '@angular/core';
import { FormControl, ControlValueAccessor, NgControl } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Subject } from 'rxjs';
import { FocusMonitor } from '@angular/cdk/a11y';
import { tap, map } from 'rxjs/operators';
import { isNumeric, isEmptyInputValue } from '../utilities/utility-functions';

@Component({
	selector: 'rpshared-currency-input',
	templateUrl: './currency-input.component.html',
	styleUrls: ['./currency-input.component.scss'],
	providers: [
		{
			provide: MatFormFieldControl,
			useExisting: CurrencyInputComponent,
		},
	],
	host: {
		'[class.example-floating]': 'shouldLabelFloat',
		'[id]': 'id',
		'(blur)': 'onTouched()',
	},
})
export class CurrencyInputComponent
	implements
		MatFormFieldControl<string>,
		OnDestroy,
		DoCheck,
		ControlValueAccessor {
	static nextId = 0;
	stateChanges = new Subject<void>();
	focused = false;
	controlType = 'currency-input';
	id = `currency-input-${CurrencyInputComponent.nextId++}`;
	describedBy = '';
	formControl = new FormControl('', { updateOn: 'blur' }) as FormControl;

	@Input()
	get placeholder(): string {
		return this._placeholder;
	}
	set placeholder(value: string) {
		this._placeholder = value;
		this.stateChanges.next();
	}
	private _placeholder: string;

	@Input()
	get required(): boolean {
		return this._required;
	}
	set required(value: boolean) {
		this._required = coerceBooleanProperty(value);
		this.stateChanges.next();
	}
	private _required = false;

	@Input()
	get disabled(): boolean {
		return this._disabled;
	}
	set disabled(value: boolean) {
		this._disabled = coerceBooleanProperty(value);
		this._disabled ? this.formControl.disable() : this.formControl.enable();
		this.stateChanges.next();
	}
	private _disabled = false;

	@Input()
	get readonly(): boolean {
		return this._readonly;
	}
	set readonly(value: boolean) {
		this._readonly = coerceBooleanProperty(value);
		this.stateChanges.next();
	}
	private _readonly = false;

	@Input()
	get value(): string {
		return this.formControl.value;
	}
	set value(value: string | null) {
		this.formControl.setValue(value, { emitEvent: false });
		this.stateChanges.next();
	}

	get empty() {
		return isEmptyInputValue(this.formControl.value);
	}

	get shouldLabelFloat() {
		return this.focused || !this.empty;
	}

	get errorState() {
		return this.ngControl.invalid && this.ngControl.touched;
	}

	constructor(
		private fm: FocusMonitor,
		private elRef: ElementRef<HTMLElement>,
		@Optional() @Self() public ngControl: NgControl
	) {
		if (this.ngControl != null) {
			this.ngControl.valueAccessor = this;
		}

		fm.monitor(elRef, true).subscribe((origin) => {
			this.focused = !!origin;

			if (!origin) {
				this.onTouched();
			}

			this.stateChanges.next();
		});
	}

	ngDoCheck() {
		this.stateChanges.next();
	}

	ngOnDestroy() {
		this.stateChanges.complete();
		this.fm.stopMonitoring(this.elRef);
	}

	setDisabledState(isDisabled: boolean) {
		isDisabled ? this.formControl.disable() : this.formControl.enable();
	}

	setDescribedByIds(ids: string[]) {
		this.describedBy = ids.join(' ');
	}

	onContainerClick(event: MouseEvent) {
		if ((event.target as Element).tagName.toLowerCase() !== 'input') {
			// tslint:disable-next-line:no-non-null-assertion
			this.elRef.nativeElement.querySelector('input')!.focus();
		}
		this.stateChanges.next();
	}

	// model --> view
	writeValue(value: string | number): void {
		this.formControl.setValue(this.formatValue(value), {
			emitEvent: false,
		});
	}

	formatValue(value: string | number): string | number {
		if (value || isNumeric(value)) {
			value = value.toString().replace(/\,/g, '');
			const format = value.match(/^\$/);
			if (format && format.index === 0) {
				value = value.replace(/^\$/, '');
			}
			if (isNumeric(value)) {
				return parseFloat(value).toLocaleString('en-US', {
					style: 'currency',
					currency: 'USD',
				});
			}
		}
		return value;
	}

	// view --> model
	registerOnChange(fn: (value: string) => void) {
		this.formControl.valueChanges
			.pipe(
				tap((value) => this.writeValue(value)),
				map((value) => (value ? value.replace(/\$|,/g, '') : null)),
				map((value) =>
					isNumeric(value) ? parseFloat(value).toFixed(2) : value
				)
			)
			.subscribe(fn);
	}

	registerOnTouched(fn: () => void) {
		this.onTouched = fn;
	}

	onTouched: () => void = () => {};
}
